import {
  branchesService,
  cargoTypesService,
  carrierCodesService,
  carrierServicesService,
  carriersService,
  currenciesService,
  equipmentTypesService,
  pricingTypesService,
  tariffTypesService,
  transportationModesService,
} from '@/modules/entities';

import type { Pricing, PricingApi, Pricings, PricingsApi, PricingsFilters, PricingsFiltersApi } from '../../models';

export const normalizePricing = async (item: PricingApi): Promise<Pricing> => {
  const [
    branch,
    cargoType,
    carrier,
    currency,
    packingCode,
    pricingBasis,
    pricingType,
    service,
    equipmentType,
    transportationMode,
  ] = await Promise.all([
    item.branchKey ? branchesService.find(String(item.branchKey)) : null,
    item.cargoType ? cargoTypesService.find(item.cargoType) : null,
    item.carrierKey ? carriersService.find(String(item.carrierKey)) : null,
    item.currencyCode ? currenciesService.find(item.currencyCode) : null,
    item.packingCode ? carrierCodesService.find(item.packingCode) : null,
    item.pricingBasis ? tariffTypesService.find(item.pricingBasis) : null,
    item.pricingType ? pricingTypesService.find(item.pricingType) : null,
    item.serviceKey ? carrierServicesService.find(String(item.serviceKey)) : null,
    item.trailerKey ? equipmentTypesService.find(String(item.trailerKey)) : null,
    item.transportationModeKey ? transportationModesService.find(item.transportationModeKey) : null,
  ]);

  return {
    ...item,
    branch,
    cargoType,
    carrier,
    createdBy: item.createdBy ?? null,
    currency,
    equipmentType,
    id: String(item.pricingKey),
    modifiedBy: item.modifiedBy ?? null,
    packingCode,
    pricingBasis,
    pricingType,
    service,
    transportationMode,
  };
};

// URL params -> form objects
export const normalizePricingsFilters = async (filters: PricingsFiltersApi): Promise<PricingsFilters> => {
  const [
    branch,
    cargoType,
    carrier,
    currency,
    packingCode,
    pricingBasis,
    pricingType,
    service,
    equipmentType,
    transportationMode,
  ] = await Promise.all([
    filters.branchKey ? branchesService.find(String(filters.branchKey)) : null,
    filters.cargoType ? cargoTypesService.find(filters.cargoType) : null,
    filters.carrierKey ? carriersService.find(String(filters.carrierKey)) : null,
    filters.currencyCode ? currenciesService.find(filters.currencyCode) : null,
    filters.packageType ? carrierCodesService.find(filters.packageType) : null,
    filters.pricingBasis ? tariffTypesService.find(filters.pricingBasis) : null,
    filters.pricingType ? pricingTypesService.find(filters.pricingType) : null,
    filters.serviceKey ? carrierServicesService.find(String(filters.serviceKey)) : null,
    filters.trailerKey ? equipmentTypesService.find(String(filters.trailerKey)) : null,
    filters.modeType ? transportationModesService.find(filters.modeType) : null,
  ]);

  return {
    branch,
    cargoType,
    carrier,
    currency,
    effectiveFrom: filters.effectiveDate || '',
    effectiveTo: filters.expirationDate,
    equipmentType,
    packingCode,
    pricingBasis,
    pricingKey: filters.pricingKey != null ? String(filters.pricingKey) : null,
    pricingType,
    service,
    transportationMode,
  };
};

// Form objects -> URL / API params
export const normalizePricingsFiltersUpdate = (filters: PricingsFilters): PricingsFiltersApi => ({
  branchKey: filters.branch ? Number(filters.branch.id) : undefined,
  cargoType: filters.cargoType ? String(filters.cargoType.id) : undefined,
  carrierKey: filters.carrier ? Number(filters.carrier.id) : undefined,
  currencyCode: filters.currency ? String(filters.currency.id) : undefined,
  effectiveDate: filters.effectiveFrom,
  expirationDate: filters.effectiveTo,
  modeType: filters.transportationMode ? String(filters.transportationMode.id) : undefined,
  packageType: filters.packingCode ? String(filters.packingCode.id) : undefined,
  pricingBasis: filters.pricingBasis ? String(filters.pricingBasis.id) : undefined,
  pricingKey: filters.pricingKey ? Number(filters.pricingKey) : undefined,
  pricingType: filters.pricingType ? String(filters.pricingType.id) : undefined,
  serviceKey: filters.service ? Number(filters.service.id) : undefined,
  trailerKey: filters.equipmentType ? Number(filters.equipmentType.id) : undefined,
});

export const normalizePricings = async (response: PricingsApi): Promise<Pricings> => ({
  items: await Promise.all(response.items.map(normalizePricing)),
  totalCount: response.totalCount,
});
